import { Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/store/authSlice";

// Layout components
import Navbar from "@/components/common/Navbar";
import Sidebar from "@/components/common/Sidebar";
import ProtectedRoute from "@/components/common/ProtectedRoute";
import ScrollToTop from "@/components/common/ScrollToTop";

// Pages
import LandingPage from "@/pages/LandingPage";
// import FeaturesPage from "@/pages/FeaturesPage"; // disabled per stakeholder feedback; uncomment to restore
import PublicTaxonomyPage from "@/pages/PublicTaxonomyPage"; // public taxonomy explorer (replaces Fitur tab)
import PublicStrukturPage from "@/pages/PublicStrukturPage";
import PnbpPage from "@/pages/PnbpPage";
import StakeholderPage from "@/pages/StakeholderPage";
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/legal/TermsOfServicePage";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import DashboardExecutivePage from "@/pages/dashboard/DashboardExecutivePage";
import DocumentListPage from "@/pages/documents/DocumentListPage";
import DocumentDetailPage from "@/pages/documents/DocumentDetailPage";
import DocumentUploadPage from "@/pages/documents/DocumentUploadPage";
import DocumentEditPage from "@/pages/documents/DocumentEditPage";
import DocumentTreePage from "@/pages/documents/DocumentTreePage";
import DocumentMatrixPage from "@/pages/documents/DocumentMatrixPage";
import ConnectionsPage from "@/pages/settings/ConnectionsPage";
import SearchPage from "@/pages/search/SearchPage";
import UserListPage from "@/pages/users/UserListPage";
import UserCreatePage from "@/pages/users/UserCreatePage";
import UserDetailPage from "@/pages/users/UserDetailPage";
import UserEditPage from "@/pages/users/UserEditPage";
import MasterDataPage from "@/pages/master/MasterDataPage";
import TagListPage from "@/pages/tags/TagListPage";
import AuditLogPage from "@/pages/audit/AuditLogPage";
import KnowledgeGraphPage from "@/pages/graph/KnowledgeGraphPage";
import KnowledgePage from "@/pages/knowledge/KnowledgePage";
import PnbpStrukturPage from "@/pages/pnbp/PnbpStrukturPage";
import PnbpKategoriPage from "@/pages/pnbp/PnbpKategoriPage";
import PnbpClusterPage from "@/pages/master/PnbpClusterPage";
import StrukturPnbpEditPage from "@/pages/content/StrukturPnbpEditPage";
import TaxonomyContentEditorPage from "@/pages/master/TaxonomyContentEditorPage";
import TaxonomyMasterPage from "@/pages/master/TaxonomyMasterPage";
import DuplicatesPage from "@/pages/admin/DuplicatesPage";

// Layout wrapper for authenticated pages
function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}

export default function App() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public route */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage />
            )
          }
        />

        <Route
          path="/register"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <RegisterPage />
            )
          }
        />

        {/* Public landing page */}
        <Route path="/" element={<LandingPage />} />
        {/* <Route path="/features" element={<FeaturesPage />} /> */}
        <Route path="/taksonomi" element={<PublicTaxonomyPage />} />
        <Route path="/struktur" element={<PublicStrukturPage />} />
        <Route path="/pnbp" element={<PnbpPage />} />
        <Route path="/pnbp-objek" element={<PnbpKategoriPage />} />
        <Route path="/stakeholder" element={<StakeholderPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />

        {/* Protected routes */}

        <Route
          path="/dashboard/executive"
          element={
            <ProtectedRoute roles={["ADMIN", "LEADER"]}>
              <AppLayout>
                <DashboardExecutivePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DashboardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DocumentListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/connections"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <ConnectionsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents/matrix"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DocumentMatrixPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents/tree"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DocumentTreePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents/upload"
          element={
            <ProtectedRoute roles={["ADMIN", "UPLOADER", "MANAGER", "TREASURER", "USER"]}>
              <AppLayout>
                <DocumentUploadPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DocumentDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents/:id/edit"
          element={
            <ProtectedRoute roles={["ADMIN", "UPLOADER", "MANAGER", "TREASURER", "USER"]}>
              <AppLayout>
                <DocumentEditPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SearchPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/knowledge"
          element={
            <ProtectedRoute>
              <AppLayout>
                <KnowledgePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/knowledge/graph"
          element={
            <ProtectedRoute>
              <AppLayout>
                <KnowledgeGraphPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/users"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <UserListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/create"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <UserCreatePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/:id"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <UserDetailPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/users/:id/edit"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <UserEditPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/master"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <MasterDataPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/master/pnbp-clusters"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <PnbpClusterPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/content/struktur-pnbp"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <StrukturPnbpEditPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/taxonomy-content"
          element={
            <ProtectedRoute roles={["ADMIN", "UPLOADER"]}>
              <AppLayout>
                <TaxonomyContentEditorPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/master/taxonomy"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <TaxonomyMasterPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/duplikat"
          element={
            <ProtectedRoute roles={["ADMIN"]}>
              <AppLayout>
                <DuplicatesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/tags"
          element={
            <ProtectedRoute roles={["ADMIN", "MANAGER"]}>
              <AppLayout>
                <TagListPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/audit"
          element={
            <ProtectedRoute roles={["ADMIN", "AUDITOR"]}>
              <AppLayout>
                <AuditLogPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/pnbp-struktur"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PnbpStrukturPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </>
  );
}
